// Recharge page with multi-company PayPal routing.
// - Displays which company will receive the funds (USA / UK / EU)
// - When admin enables `apply_tax` on the routed company, breakdown is shown.
// - Real PayPal SDK integration: the routed company's Client ID is loaded
//   dynamically via the @paypal/react-paypal-js provider.
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { CreditCard, Mail, Phone, AlertCircle, Check, Loader2, Globe } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Recharge limits — sourced from `app_config.recharge_limits` so admins
// can tune the floor without redeploying. Defaults match the server
// fallback in `routes/service_flags.py:DEFAULT_RECHARGE_LIMITS`. The
// state values are updated at mount time from `/api/service/recharge-limits`.
const DEFAULT_MIN_RECHARGE = 5;
const DEFAULT_MAX_RECHARGE = 10000;

const COLOR_RING = {
  blue:    { border: 'border-blue-500',    bg: 'bg-blue-50' },
  purple:  { border: 'border-purple-500',  bg: 'bg-purple-50' },
  green:   { border: 'border-green-500',   bg: 'bg-green-50' },
  indigo:  { border: 'border-indigo-500',  bg: 'bg-indigo-50' },
  cyan:    { border: 'border-cyan-500',    bg: 'bg-cyan-50' },
  amber:   { border: 'border-amber-500',   bg: 'bg-amber-50' },
  rose:    { border: 'border-rose-500',    bg: 'bg-rose-50' },
};

// Visual flag per company role (USA / UK / EU)
const ROLE_META = {
  USA_COMPANY: { flag: '🇺🇸', label: 'USA' },
  UK_COMPANY:  { flag: '🇬🇧', label: 'United Kingdom' },
  EU_COMPANY:  { flag: '🇪🇺', label: 'European Union' },
};

const TILE_TONE = {
  slate:   { label: 'text-slate-500',   value: 'text-slate-800',   border: 'border-slate-200',   bg: 'bg-slate-50' },
  emerald: { label: 'text-emerald-600', value: 'text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50' },
  rose:    { label: 'text-rose-600',    value: 'text-rose-700',    border: 'border-rose-200',    bg: 'bg-rose-50' },
  cyan:    { label: 'text-cyan-700',    value: 'text-cyan-800',    border: 'border-cyan-200',    bg: 'bg-cyan-50' },
};

// Single tile used in the wallet-context card (Current / Reserved /
// Available / After recharge).
const BalanceTile = ({ label, value, testid, tone = 'slate' }) => {
  const c = TILE_TONE[tone] || TILE_TONE.slate;
  return (
    <div className={`p-3 rounded-lg border ${c.border} ${c.bg}`} data-testid={testid}>
      <div className={`text-[10px] sm:text-[11px] uppercase tracking-wide font-bold ${c.label}`}>{label}</div>
      <div className={`text-lg sm:text-xl font-black mt-0.5 ${c.value}`}>{value}</div>
    </div>
  );
};

export default function RechargeAccount({ token, onRecharged, onNavigate }) {
  const { t } = useTranslation('recharge');
  const { t: tCommon } = useTranslation('common');
  const tNav = (k) => tCommon(`nav.${k}`);
  const [selectedMethod, setSelectedMethod] = useState('');
  const [amount, setAmount] = useState(() => {
    // If we got here from "Top up to place order" on the New Order page,
    // pre-fill the amount the customer chose so they only have to click pay.
    try { return sessionStorage.getItem('pendingTopupAmount') || ''; } catch (e) { return ''; }
  });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [packages, setPackages] = useState([]);
  const [rechargeDisabled, setRechargeDisabled] = useState(false);
  const [globalFlags, setGlobalFlags] = useState(null);  // { recharge_disabled, recharge_disabled_message }
  const [routing, setRouting] = useState(null);   // resolved company info
  const [routingLoading, setRoutingLoading] = useState(false);
  // Current user balance — used to enforce the negative-balance bonus rule.
  // When balance < 0 no bonus is granted by the backend; we mirror that in
  // the UI so the customer sees the policy before paying.
  const [userBalance, setUserBalance] = useState(null);
  const [userReserved, setUserReserved] = useState(0);
  // Recent top-ups (last 3) — displayed under the input as social proof
  // and a quick way to confirm a prior recharge actually credited.
  const [recentRecharges, setRecentRecharges] = useState([]);
  // Stripe-return polling state — set when the URL contains `?rp_sid=...`.
  const [stripePoll, setStripePoll] = useState(null);  // {status, amount, ...} | null
  // Limits sourced from `/api/service/recharge-limits` so admins can tune
  // the floor without redeploying. Defaults match the server fallback.
  const [minRecharge, setMinRecharge] = useState(DEFAULT_MIN_RECHARGE);
  const [maxRecharge, setMaxRecharge] = useState(DEFAULT_MAX_RECHARGE);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/service/recharge-limits`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!cancelled && j) {
          if (Number.isFinite(j.min_recharge)) setMinRecharge(j.min_recharge);
          if (Number.isFinite(j.max_recharge)) setMaxRecharge(j.max_recharge);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Shadow constants so the rest of the file reads naturally — these
  // refer to the latest fetched values on every render. (Renaming all
  // 7 usages would balloon the diff.)
  const MIN_RECHARGE = minRecharge;
  const MAX_RECHARGE = maxRecharge;

  // If we returned from Stripe, poll the status endpoint. Retries 10x with
  // back-off to give the webhook time to fire (the poller credits the
  // balance itself if the webhook is delayed).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const sid = sp.get('rp_sid');
    if (!sid) return;
    let cancelled = false;
    (async () => {
      setStripePoll({ loading: true });
      const headers = { Authorization: `Bearer ${token}` };
      for (let attempt = 0; attempt < 10; attempt++) {
        if (cancelled) return;
        try {
          const r = await fetch(`${API_URL}/api/stripe/status/${sid}`, { headers });
          if (r.ok) {
            const j = await r.json();
            if (j.credited || j.status === 'paid') {
              setStripePoll({ loading: false, ...j });
              setSuccess({
                paid: j.amount,
                bonus: j.bonus || 0,
                total: (j.amount || 0) + (j.bonus || 0),
                method: 'stripe',
                intended_bonus: j.intended_bonus,
                bonus_skipped_reason: j.bonus_skipped_reason,
              });
              if (onRecharged) onRecharged();
              // clean URL
              window.history.replaceState({}, '', window.location.pathname);
              return;
            }
          }
        } catch (_) { /* retry */ }
        await new Promise((res) => setTimeout(res, 1500 + attempt * 500));
      }
      if (!cancelled) {
        setStripePoll({ loading: false, status: 'pending', error: 'Payment is still processing. It may take a few minutes — your balance will update automatically.' });
      }
    })();
    return () => { cancelled = true; };
  }, [token, onRecharged]);

  // Auto-redirect back to New Order page when payment lands and a pending
  // order is waiting in sessionStorage. Fires whenever `success` flips to a
  // truthy value (Stripe webhook return, manual recharge, PayPal capture).
  useEffect(() => {
    if (!success) return;
    let pending = null;
    try { pending = sessionStorage.getItem('pendingOrder'); } catch (e) { /* ignore */ }
    if (!pending) return;
    // Delay enough so the customer reads the green "Payment received"
    // confirmation, then jump back to the wizard with the form rehydrated.
    const t = setTimeout(() => {
      try { sessionStorage.removeItem('pendingTopupAmount'); } catch (e) { /* ignore */ }
      if (onNavigate) onNavigate('new-order');
    }, 1800);
    return () => clearTimeout(t);
  }, [success, onNavigate]);

  // Fetch global service flags (maintenance banner)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/service/flags`);
        if (r.ok) setGlobalFlags(await r.json());
      } catch (_) { /* silent */ }
    })();
  }, []);

  const paymentMethods = [
    { id: 'paypal', name: 'PayPal',             icon: '💳', description: 'Fast & Secure',                         color: 'blue'   },
    { id: 'stripe', name: 'Cards (Stripe)',     icon: '💳', description: 'Visa, Mastercard, Apple/Google Pay',    color: 'indigo' },
    { id: 'bank',   name: 'Bank Transfer',      icon: '🏦', description: 'Direct transfer',                       color: 'green'  },
  ];

  // Re-fetch user from API so we have the latest `recharge_disabled` flag
  // AND the current `balance` so the negative-balance bonus warning renders
  // correctly. Re-fetched after every successful recharge.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const me = await r.json();
        setRechargeDisabled(!!me.recharge_disabled);
        const bal = typeof me.balance === 'number'
          ? me.balance
          : parseFloat(me.balance ?? 0);
        setUserBalance(Number.isFinite(bal) ? bal : 0);
        const res = typeof me.reserved === 'number'
          ? me.reserved
          : parseFloat(me.reserved ?? 0);
        setUserReserved(Number.isFinite(res) ? res : 0);
      } catch (_) { /* silent */ }
    })();
  }, [token, success]);

  // Recent top-ups (last 3 successful recharges) — small "you've topped
  // up X times" feedback under the input.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/transactions/?limit=50`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) return;
        const data = await r.json();
        const onlyRecharges = (data || [])
          .filter((t) => (t.type === 'recharge') && (t.direction === 'to_user'))
          .slice(0, 3);
        setRecentRecharges(onlyRecharges);
      } catch (_) { /* silent */ }
    })();
  }, [token, success]);

  // Load admin-defined bonus packages (only enabled ones)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/bonus-packages`);
        if (!r.ok) return;
        const data = await r.json();
        setPackages((data || []).filter((p) => p.enabled !== false));
      } catch (_) { /* silent */ }
    })();
  }, []);

  // Resolve which company will receive the payment whenever the amount changes
  // (this also triggers the tax breakdown if the company has `apply_tax` on).
  useEffect(() => {
    if (selectedMethod !== 'paypal') { setRouting(null); return; }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { setRouting(null); return; }

    const ctrl = new AbortController();
    setRoutingLoading(true);
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/payments/resolve-routing?amount=${amt}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        if (!r.ok) { setRouting(null); return; }
        setRouting(await r.json());
      } catch (e) {
        if (e.name !== 'AbortError') setRouting(null);
      } finally {
        setRoutingLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [amount, selectedMethod, token]);

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg.amount);
    setAmount(pkg.amount.toString());
  };

  // Bonus is determined by the highest tier whose `amount` <= entered amount.
  const sortedPackages = useMemo(
    () => [...packages].sort((a, b) => a.amount - b.amount),
    [packages],
  );
  const activeTier = useMemo(() => {
    const a = parseFloat(amount) || 0;
    if (a <= 0 || sortedPackages.length === 0) return null;
    let tier = null;
    for (const p of sortedPackages) {
      if (a >= p.amount) tier = p;
      else break;
    }
    return tier;
  }, [amount, sortedPackages]);
  // Backend rule: bonus only granted when pre-recharge balance >= 0.
  // We mirror that in the UI so customers see the rule BEFORE they pay.
  // `userBalance === null` (still loading) is treated as "allowed" so the
  // bonus chip doesn't flicker on first render.
  const bonusBlockedByDebt = userBalance !== null && userBalance < 0;
  const tierBonus = activeTier ? activeTier.bonus : 0;
  const effectiveBonus = bonusBlockedByDebt ? 0 : tierBonus;

  // ---- CANONICAL RECHARGE STATE ---------------------------------------
  // Single source of truth for the entered amount + validation state.
  // Every visible block (input feedback, summary, Stripe button text,
  // PayPal button text, backend payload) reads from THESE values. No
  // other place may compute a different "effective amount". This is what
  // killed the snap-to-€1 bug.
  const amtCanonical = parseFloat(amount);
  const isAmountValid = (
    Number.isFinite(amtCanonical) &&
    amtCanonical >= MIN_RECHARGE &&
    amtCanonical <= MAX_RECHARGE
  );
  let amountError = '';
  if (amount && Number.isFinite(amtCanonical)) {
    if (amtCanonical < MIN_RECHARGE) amountError = `Minimum recharge amount is €${MIN_RECHARGE}`;
    else if (amtCanonical > MAX_RECHARGE) amountError = `Maximum recharge amount is €${MAX_RECHARGE.toLocaleString()}`;
  } else if (amount && !Number.isFinite(amtCanonical)) {
    amountError = 'Please enter a valid number';
  }

  // Dynamic CTA label (purely informational — the actual buttons live
  // further down the tree and read the same canonical state).
  const ctaLabel = (() => {
    if (rechargeDisabled) return 'Recharge disabled';
    if (!selectedMethod)  return t('cta.select_payment_method', { defaultValue: 'Select payment method' });
    if (!amount)          return 'Enter amount';
    if (!isAmountValid)   return amountError || 'Enter a valid amount';
    if (selectedMethod === 'paypal') return `Continue to PayPal · €${amtCanonical.toFixed(2)}`;
    if (selectedMethod === 'stripe') return `Pay by Card · €${amtCanonical.toFixed(2)}`;
    if (selectedMethod === 'bank')   return 'Open bank-transfer instructions';
    return `Top up €${amtCanonical.toFixed(2)}`;
  })();

  // Recharge is provider-verified only: Stripe checkout (own button) and
  // PayPal capture. The old mocked /transactions/recharge call was removed
  // after it was abused to self-credit a balance (06.10.2026).
  const handleRecharge = async () => {
    if (selectedMethod === 'bank') {
      alert('Please contact info@auto-remap.com for bank-transfer instructions.');
      return;
    }
    if (selectedMethod === 'paypal') {
      alert('Please complete your payment via the PayPal button above.');
      return;
    }
    alert('Please complete your payment with the Stripe button below.');
  };

  const amt = parseFloat(amount) || 0;
  const taxAmount = routing?.tax_amount ?? 0;
  const totalWithTax = routing?.total_with_tax ?? amt;
  // Routing card visibility is admin-controlled via the
  // `recharge_show_routing` service flag (default OFF).
  // Toggle it on at: Admin → Settings → Service Flags → "Show payment
  // routing on Recharge page" (or PUT /api/service/flags
  // {"recharge_show_routing": true}). Hidden by default because the
  // legal entity processing the payment is typically not info the
  // customer needs upfront — VAT is still shown when applicable.
  const routingVisible = !!globalFlags?.recharge_show_routing;
  const showRoutingCard = routingVisible && selectedMethod === 'paypal' && amt > 0;

  return (
    <div className="space-y-4 sm:space-y-6 max-w-[88rem] mx-auto p-4 md:p-6" data-testid="recharge-account">
      {/* GLOBAL maintenance banner — admin disabled recharges entirely */}
      {globalFlags?.recharge_disabled && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex items-start gap-4" data-testid="recharge-global-disabled-banner">
          <div className="w-12 h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-white text-2xl font-bold">⚠</div>
          <div>
            <h3 className="text-lg font-bold text-amber-800 mb-1">{t('messages.failed')}</h3>
            <p className="text-sm text-amber-800 whitespace-pre-line">{globalFlags.recharge_disabled_message}</p>
          </div>
        </div>
      )}

      {/* Recharge disabled banner */}
      {rechargeDisabled && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-5 flex items-start gap-4" data-testid="recharge-disabled-banner">
          <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 text-white text-2xl font-bold">⛔</div>
          <div>
            <h3 className="text-lg font-bold text-red-700 mb-1">Recharge is currently disabled for your account</h3>
            <p className="text-sm text-red-700">
              Top-ups have been temporarily restricted by an administrator. Existing balance and orders are unaffected.
              If you believe this is a mistake, please contact support — we'll review your account right away.
            </p>
          </div>
        </div>
      )}

      {/* Negative-balance bonus warning — visible BEFORE the customer pays
          so they understand the rule and aren't surprised when their bonus
          tier shows €0. The banner is dismissable only by clearing the
          debt (balance >= 0) on the next refresh. */}
      {bonusBlockedByDebt && (
        <div
          className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 sm:p-5 flex items-start gap-3 sm:gap-4"
          data-testid="bonus-blocked-banner"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-white">
            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-base font-bold text-amber-900 mb-1">
              {t('bonus_blocked.title', { defaultValue: 'No bonus while your balance is negative' })}
            </h3>
            <p className="text-xs sm:text-sm text-amber-800 leading-snug">
              <Trans
                i18nKey="bonus_blocked.body"
                ns="recharge"
                values={{ balance: `€${userBalance.toFixed(2)}` }}
                components={{
                  bal: <span className="font-bold whitespace-nowrap" data-testid="bonus-blocked-balance" />,
                  em: <span className="font-semibold" />,
                }}
                defaults="Your current balance is <bal>{{balance}}</bal>. Recharge bonuses are only granted once your wallet is back to €0 or above. The full paid amount will still be added — <em>bonus tiers are shown disabled below</em>."
              />
            </p>
          </div>
        </div>
      )}

      {/* Banner */}
      <div className={`bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-4 sm:p-6 text-white shadow-lg ${rechargeDisabled || globalFlags?.recharge_disabled ? 'opacity-60 pointer-events-none select-none' : ''}`}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm flex-shrink-0">
            <CreditCard className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">{t('title')}</h2>
            <p className="text-sm sm:text-base opacity-90">Pay securely and receive bonus credits when eligible.</p>
          </div>
        </div>
      </div>

      {/* Wallet context card — shows current balance, reserved on open
          orders, available-to-spend AND the projected balance AFTER
          this recharge. Lives BEFORE the payment-method picker so the
          customer can sanity-check their entered amount. */}
      {userBalance !== null && !rechargeDisabled && !globalFlags?.recharge_disabled && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="balance-context-card">
          <BalanceTile label={t('current', { defaultValue: 'Current' })} value={`€${userBalance.toFixed(2)}`} testid="balance-current"
            tone={userBalance < 0 ? 'rose' : 'slate'} />
          <BalanceTile label={t('reserved_open_orders', { defaultValue: 'Reserved (open orders)' })} value={`€${userReserved.toFixed(2)}`} testid="balance-reserved" tone="slate" />
          <BalanceTile label={t('available', { defaultValue: 'Available' })} value={`€${(userBalance - userReserved).toFixed(2)}`} testid="balance-available"
            tone={(userBalance - userReserved) < 0 ? 'rose' : 'emerald'} />
          <BalanceTile
            label={t('after_recharge', { defaultValue: 'After recharge' })}
            value={
              isAmountValid
                ? `€${(userBalance + amtCanonical + (effectiveBonus || 0)).toFixed(2)}`
                : '—'
            }
            testid="balance-after-recharge"
            tone="cyan"
          />
        </div>
      )}

      {/* Stripe return poller — shows while we confirm the payment */}
      {stripePoll && stripePoll.loading && (
        <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-5 flex items-start gap-4" data-testid="stripe-poll-loading">
          <Loader2 className="w-6 h-6 text-indigo-600 animate-spin flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-bold text-indigo-800 mb-1">Confirming your Stripe payment…</h3>
            <p className="text-sm text-indigo-700">We're verifying the transaction with Stripe and will credit your account in a moment. Please don't close this page.</p>
          </div>
        </div>
      )}
      {stripePoll && !stripePoll.loading && stripePoll.status !== 'paid' && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 flex items-start gap-4" data-testid="stripe-poll-pending">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-base font-bold text-amber-800 mb-1">Payment processing</h3>
            <p className="text-sm text-amber-700">{stripePoll.error || 'Your Stripe payment is being processed. Your balance will update automatically once confirmed.'}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border-2 border-emerald-400 rounded-xl p-4" data-testid="recharge-success">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-emerald-800">{t('messages.success')}</h3>
              <p className="text-sm text-emerald-700">
                €{success.paid.toFixed(2)} added{success.bonus > 0 && <> + €{success.bonus.toFixed(2)} bonus</>} — total <b>€{success.total.toFixed(2)}</b> credited via {success.method}.
                {success.bonus > 0 && <span className="block text-xs opacity-80 mt-0.5">Two transactions were created — one with invoice (paid), one without (bonus).</span>}
                {success.bonus_skipped_reason === 'negative_balance' && success.intended_bonus > 0 && (
                  <span
                    className="block text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1"
                    data-testid="success-bonus-skipped-note"
                  >
                    Bonus of €{success.intended_bonus.toFixed(2)} was not added because your wallet was negative before this top-up.
                  </span>
                )}
              </p>
            </div>
            {/* If we got here from "Top up to place order", surface a quick
                CTA back to the New Order page where the form is still
                pre-filled. We use sessionStorage as the trigger so it only
                appears for that specific flow. */}
            {(() => {
              let pending = null;
              try { pending = sessionStorage.getItem('pendingOrder'); } catch (e) { /* ignore */ }
              if (!pending) return null;
              return (
                <button
                  onClick={() => {
                    try { sessionStorage.removeItem('pendingTopupAmount'); } catch (e) { /* ignore */ }
                    if (onNavigate) onNavigate('new-order');
                    if (onRecharged) onRecharged();
                  }}
                  className="flex-shrink-0 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold text-sm shadow-md hover:shadow-lg transition flex items-center gap-2 animate-pulse"
                  data-testid="resume-order-btn"
                  title="You'll be sent back automatically in a moment — click to skip the wait."
                >
                  Resume order →
                </button>
              );
            })()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left: methods + amount + packages */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Methods */}
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
            <h3 className="text-base sm:text-lg font-bold text-slate-800 mb-3 sm:mb-4">{t('method')}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {paymentMethods.map((method) => {
                const ring = COLOR_RING[method.color] || COLOR_RING.blue;
                const isSel = selectedMethod === method.id;
                return (
                  <button key={method.id} onClick={() => setSelectedMethod(method.id)} data-testid={`payment-method-${method.id}`}
                    className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all text-left ${isSel ? `${ring.border} ${ring.bg}` : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                    {isSel && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-2">
                      {method.id === 'stripe' ? (
                        <div className="flex-shrink-0 flex items-center gap-1.5">
                          {/* Row of accepted card logos — communicates "cards
                              via Stripe" without an extra separate tile. */}
                          <div className="px-1.5 py-1 bg-white border border-slate-200 rounded shadow-sm text-[10px] font-black italic text-[#1a1f71] leading-none" aria-label="Visa">VISA</div>
                          <div className="flex items-center leading-none" aria-label="Mastercard">
                            <span className="w-3.5 h-3.5 rounded-full bg-[#eb001b] -mr-1" />
                            <span className="w-3.5 h-3.5 rounded-full bg-[#f79e1b] mix-blend-multiply" />
                          </div>
                        </div>
                      ) : (
                        <div className="text-3xl sm:text-4xl">{method.icon}</div>
                      )}
                      <div className="flex-1">
                        <div className="text-sm sm:text-base font-bold text-slate-800">{method.name}</div>
                        <div className="text-xs text-slate-500">{method.description}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount */}
          {selectedMethod !== 'bank' && (
            <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 shadow-sm">
              <h3 className="text-sm sm:text-base font-bold text-slate-800 mb-2 sm:mb-3">{t('amount_label')}</h3>
              <div className="relative">
                <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-xl sm:text-2xl font-bold text-slate-400">€</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  data-testid="recharge-amount-input"
                  /* Strip everything except digits and a single decimal separator —
                     prevents users from pasting thousand separators or other junk
                     while still typing the amount comfortably. */
                  onChange={(e) => {
                    const cleaned = e.target.value
                      .replace(',', '.')
                      .replace(/[^0-9.]/g, '')
                      .replace(/(\..*)\./g, '$1');
                    setAmount(cleaned);
                    setSelectedPackage(null);
                  }}
                  /* Block the scroll-wheel-changes-value misery. The previous
                     <input type="number"> let users accidentally bump the
                     amount when scrolling the page over the field. */
                  onWheel={(e) => e.target.blur()}
                  placeholder="0.00"
                  className={`w-full pl-9 sm:pl-11 pr-4 py-2.5 sm:py-3 text-xl sm:text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${amountError ? 'border-rose-400' : 'border-slate-300'}`}
                />
              </div>
              {amountError ? (
                <div className="mt-2 text-xs font-semibold text-rose-600" data-testid="recharge-amount-error">
                  {amountError}
                </div>
              ) : (
                <div className="mt-2 text-[11px] sm:text-xs text-slate-500">
                  {t('minmax_hint', { min: MIN_RECHARGE, max: MAX_RECHARGE.toLocaleString(), defaultValue: `Minimum amount: €${MIN_RECHARGE} • Maximum: €${MAX_RECHARGE.toLocaleString()}` })}
                </div>
              )}
            </div>
          )}

          {/* Packages */}
          {selectedMethod !== 'bank' && packages.length > 0 && (
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-4">
                <h3 className="text-base sm:text-lg font-bold text-slate-800">{t('packages.title')}</h3>
                {bonusBlockedByDebt ? (
                  <span
                    className="text-xs sm:text-sm text-amber-700 font-semibold bg-amber-100 px-2 py-0.5 rounded-full"
                    data-testid="bonus-packages-disabled-chip"
                  >
                    {t('bonus_blocked.paused', { defaultValue: 'Bonuses paused — debt active' })}
                  </span>
                ) : (
                  <span className="text-xs sm:text-sm text-green-600 font-medium">🎁 Get Extra Credits!</span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {sortedPackages.map((pkg) => {
                  const ring = COLOR_RING[pkg.color] || COLOR_RING.cyan;
                  const isSel = activeTier && activeTier.amount === pkg.amount;
                  const isDebtBlocked = bonusBlockedByDebt && pkg.bonus > 0;
                  return (
                    <button key={pkg.id || pkg.amount} onClick={() => handlePackageSelect(pkg)} data-testid={`bonus-package-${pkg.amount}`}
                      className={`relative p-4 sm:p-5 rounded-xl border-2 transition-all ${isSel ? `${ring.border} ${ring.bg}` : 'border-slate-200 hover:border-slate-300 bg-white'} ${isDebtBlocked ? 'opacity-70' : ''}`}>
                      {pkg.popular && (
                        <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 px-2 sm:px-3 py-0.5 sm:py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] sm:text-xs font-bold rounded-full shadow-lg">POPULAR</div>
                      )}
                      <div className="text-center">
                        <div className="text-3xl sm:text-4xl font-black text-slate-800 mb-1 sm:mb-2">€{pkg.amount}</div>
                        {isDebtBlocked ? (
                          <div
                            className="inline-block px-2 sm:px-3 py-1 bg-slate-100 text-slate-500 text-xs sm:text-sm font-bold rounded-full line-through"
                            data-testid={`bonus-package-skipped-${pkg.amount}`}
                            title="Bonus is paused while your balance is negative"
                          >
                            +€{pkg.bonus} Bonus
                          </div>
                        ) : pkg.bonus > 0 ? (
                          <div className="inline-block px-2 sm:px-3 py-1 bg-green-100 text-green-700 text-xs sm:text-sm font-bold rounded-full">+€{pkg.bonus} {t('bonus_label', { defaultValue: 'Bonus' })}</div>
                        ) : (
                          <div className="inline-block px-2 sm:px-3 py-1 bg-slate-100 text-slate-500 text-xs sm:text-sm font-medium rounded-full">{t('no_bonus', { defaultValue: 'No bonus' })}</div>
                        )}
                        <div className="mt-2 sm:mt-3 text-xs text-slate-500">
                          {isDebtBlocked ? (
                            <Trans
                              i18nKey="bonus_blocked.bonus_unavailable"
                              ns="recharge"
                              values={{ amount: pkg.amount }}
                              components={{ b: <b className="text-slate-700" /> }}
                              defaults="Bonus unavailable · You receive <b>€{{amount}}</b>"
                            />
                          ) : (
                            <Trans
                              i18nKey="you_receive_total"
                              ns="recharge"
                              values={{ amount: pkg.amount + (pkg.bonus || 0) }}
                              components={{ b: <b className="text-slate-700" /> }}
                              defaults="You receive <b>€{{amount}}</b>"
                            />
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className={`mt-3 sm:mt-4 p-3 rounded-lg border ${bonusBlockedByDebt ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className={`text-xs sm:text-sm font-medium ${bonusBlockedByDebt ? 'text-slate-600' : 'text-amber-800'}`}>
                  {bonusBlockedByDebt
                    ? t('bonus_resume_zero', { defaultValue: 'Recharge bonuses resume automatically once your balance reaches €0 or higher.' })
                    : '💡 Top up your balance using the pre-set amounts to receive an automatic bonus and discount on your next order!'}
                </div>
              </div>
            </div>
          )}

          {/* Routing Info Card — visible when PayPal + amount entered */}
          {showRoutingCard && (
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl p-4 sm:p-5 border-2 border-blue-200 shadow-sm" data-testid="paypal-routing-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-0.5">Payment will be processed by</div>
                  {routingLoading && !routing ? (
                    <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                      <Loader2 className="w-4 h-4 animate-spin" /> Resolving payment route…
                    </div>
                  ) : routing?.error ? (
                    <div className="text-sm text-red-600 font-medium" data-testid="paypal-routing-error">
                      No active payment provider available. Please contact support.
                    </div>
                  ) : routing?.company ? (
                    <>
                      <div className="text-base sm:text-lg font-bold text-slate-800 flex items-center gap-2" data-testid="paypal-routing-company">
                        <span className="text-2xl">{ROLE_META[routing.role]?.flag || '🌍'}</span>
                        <span>{routing.company.name}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {ROLE_META[routing.role]?.label || routing.company.country || ''}
                        {routing.routing_reason === 'FORCED' && (
                          <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] uppercase font-bold">Manual Override</span>
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              {/* Breakdown */}
              {routing?.company && (
                <div className="mt-4 pt-4 border-t border-blue-200/60 space-y-1.5 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal</span>
                    <span className="font-medium">€{amt.toFixed(2)}</span>
                  </div>
                  {routing.apply_tax && taxAmount > 0 && (
                    <div className="flex justify-between text-slate-600" data-testid="paypal-tax-line">
                      <span>VAT ({routing.tax_rate}%)</span>
                      <span className="font-medium">+€{taxAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-1.5 border-t border-blue-200/60 text-slate-800 font-bold">
                    <span>Total to pay</span>
                    <span className="text-blue-700" data-testid="paypal-total">€{totalWithTax.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PayPal & Stripe button blocks moved to the RIGHT column,
              directly under Payment Summary — keeps the action visible
              alongside the totals and shrinks the button to the summary
              card's width instead of the full left column. */}
        </div>

        {/* Right: summary or bank instructions */}
        <div className="space-y-4 sm:space-y-6">
          {selectedMethod === 'bank' ? (
            <div className="bg-white rounded-xl p-4 sm:p-6 border-2 border-amber-400 shadow-lg lg:sticky lg:top-[170px]">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b-2 border-amber-200">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center"><span className="text-2xl">🏦</span></div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-slate-800">Bank Transfer</h3>
                  <p className="text-xs text-amber-600 font-medium">(Invoice Required)</p>
                </div>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-slate-700">Bank transfer payments are available upon request and require an official invoice.</p>
                <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200 space-y-3">
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Email</div>
                      <a href="mailto:info@auto-remap.com" className="text-sm font-semibold text-cyan-600 hover:underline">info@auto-remap.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs text-slate-500">Phone</div>
                      <a href="tel:+34692033056" className="text-sm font-semibold text-green-600 hover:underline">+34 692 033 056</a>
                    </div>
                  </div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border-2 border-amber-300 flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-800 mb-1">Bank transfer requirements</p>
                    <ul className="text-xs text-amber-700 list-disc list-inside space-y-0.5">
                      <li>Minimum amount: <span className="font-bold">€2,000</span></li>
                      <li>Bank transfers are processed manually</li>
                      <li>Allow up to 1–3 business days for confirmation</li>
                    </ul>
                  </div>
                </div>
                <a
                  href="mailto:info@auto-remap.com?subject=Bank%20Transfer%20Request%20-%20Recharge"
                  data-testid="bank-transfer-contact-btn"
                  className="block w-full text-center bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-3 rounded-lg shadow-md transition"
                >
                  Contact support for bank-transfer instructions
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-4 sm:p-6 text-white shadow-lg" data-testid="payment-summary-card">
              <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">{t('payment_summary', { defaultValue: 'Payment Summary' })}</h3>
              <div className="space-y-3 sm:space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                  <span className="text-sm text-slate-400">{t('payment_method', { defaultValue: 'Payment Method' })}</span>
                  <span className="text-sm font-medium">{selectedMethod ? paymentMethods.find((m) => m.id === selectedMethod)?.name : t('not_selected', { defaultValue: 'Not selected' })}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                  <span className="text-sm text-slate-400">{t('amount', { defaultValue: 'Amount' })}</span>
                  <span className="text-xl font-bold text-cyan-400" data-testid="summary-amount">{amount ? `€${parseFloat(amount).toFixed(2)}` : '€0.00'}</span>
                </div>
                {effectiveBonus > 0 && (
                  <>
                    <div className="flex justify-between items-center pb-3 border-b border-slate-700">
                      <span className="text-sm text-green-400 font-medium">Bonus</span>
                      <span className="text-lg font-bold text-green-400" data-testid="summary-bonus">+€{effectiveBonus}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 bg-green-500/10 rounded-lg px-3">
                      <span className="text-sm font-medium text-green-400">Total You Get</span>
                      <span className="text-2xl font-black text-green-400" data-testid="summary-total">€{(parseFloat(amount || 0) + effectiveBonus).toFixed(2)}</span>
                    </div>
                  </>
                )}
                {bonusBlockedByDebt && tierBonus > 0 && (
                  <div
                    className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-400/40"
                    data-testid="summary-bonus-skipped"
                  >
                    <AlertCircle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                    <span className="text-xs text-amber-200 leading-snug">
                      Bonus of <b>€{tierBonus}</b> not applied — recharges grant bonus only when your balance is €0 or above.
                    </span>
                  </div>
                )}

                {/* Dynamic CTA hint — drives the customer to the next
                    action they need to take. Reads the canonical state
                    so it can never disagree with the actual button. */}
                <div
                  className={`mt-1 px-3 py-2.5 rounded-lg text-center font-semibold text-sm ${
                    isAmountValid && selectedMethod
                      ? 'bg-cyan-500/15 text-cyan-200 border border-cyan-400/40'
                      : 'bg-slate-700/60 text-slate-300 border border-slate-600'
                  }`}
                  data-testid="summary-cta-hint"
                >
                  {ctaLabel}
                </div>
              </div>
              {selectedMethod !== 'paypal' && selectedMethod !== 'stripe' && (
                <button onClick={handleRecharge} disabled={submitting || !selectedMethod || !amount || parseFloat(amount) <= 0} data-testid="recharge-pay-btn"
                  className={`w-full mt-4 py-2.5 sm:py-3 rounded-lg font-bold text-sm sm:text-base transition-all flex items-center justify-center gap-2 ${selectedMethod && amount && parseFloat(amount) > 0 && !submitting ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-md' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}>
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {submitting ? 'Processing…' : selectedMethod && amount ? `Pay €${parseFloat(amount).toFixed(2)}` : 'Select Method & Amount'}
                </button>
              )}
              {selectedMethod === 'paypal' && (
                <div className="mt-4 p-3 bg-blue-500/15 border border-blue-400/40 rounded-lg text-xs text-blue-200">
                  Use the PayPal button below to complete your payment.
                </div>
              )}
              {selectedMethod === 'stripe' && (
                <div className="mt-4 p-3 bg-indigo-500/15 border border-indigo-400/40 rounded-lg text-xs text-indigo-200" data-testid="stripe-hint-summary">
                  Use the Stripe button below — you'll be redirected to Stripe's secure checkout page.
                </div>
              )}
              <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2 text-xs text-slate-400">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
                <span>Secure Payment • SSL Encrypted</span>
              </div>
            </div>
          )}

          {/* Payment buttons — sit right under the Payment Summary so
              the action is glued to the totals. Constrained to the
              summary's column width instead of the full left column. */}
          {selectedMethod === 'paypal' && isAmountValid && routing?.company && !success && (
            <PayPalRechargeButtons
              token={token}
              amount={amtCanonical}
              bonus={effectiveBonus}
              summaryAmount={amtCanonical}
              onSuccess={(j) => {
                setSuccess({
                  paid: j.paid, bonus: j.bonus, total: j.credited, method: 'paypal',
                  intended_bonus: j.intended_bonus,
                  bonus_skipped_reason: j.bonus_skipped_reason,
                });
                setAmount(''); setSelectedPackage(null);
                if (onRecharged) onRecharged();
              }}
            />
          )}
          {selectedMethod === 'stripe' && isAmountValid && !success && (
            <StripeRechargeButton
              token={token}
              amount={amtCanonical}
              bonus={effectiveBonus}
              summaryAmount={amtCanonical}
              minRecharge={MIN_RECHARGE}
            />
          )}

          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <h4 className="text-sm font-bold text-blue-800 mb-2">💼 {t('why_recharge', { defaultValue: 'Why Recharge?' })}</h4>
            <ul className="text-xs text-blue-700 space-y-1.5">
              <li>✓ {t('benefit_faster', { defaultValue: 'Faster order processing' })}</li>
              <li>✓ {t('benefit_no_delays', { defaultValue: 'No payment delays' })}</li>
              <li>✓ {t('benefit_bonuses', { defaultValue: 'Automatic bonuses' })}</li>
              <li>✓ {t('benefit_24_7', { defaultValue: '24/7 available balance' })}</li>
            </ul>
          </div>

          {/* Recent top-ups — last 3 successful recharges so customers
              can see their history without leaving the page. */}
          {recentRecharges.length > 0 && (
            <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="recent-recharges-card">
              <h4 className="text-sm font-bold text-slate-800 mb-3">Recent top-ups</h4>
              <ul className="space-y-2">
                {recentRecharges.map((t) => {
                  const created = t.created_at
                    ? new Date(t.created_at)
                    : null;
                  const label = created
                    ? created.toLocaleDateString(undefined, {
                        day: '2-digit', month: 'short', year: 'numeric',
                      })
                    : '';
                  const method = t.payment_method || t.provider || 'manual';
                  return (
                    <li
                      key={t.id}
                      data-testid={`recent-recharge-${t.id}`}
                      className="flex items-center justify-between text-xs sm:text-sm"
                    >
                      <div className="min-w-0 truncate">
                        <span className="font-semibold text-slate-700">€{Number(t.amount).toFixed(2)}</span>
                        <span className="text-slate-400"> · {method}</span>
                      </div>
                      <span className="text-slate-500 ml-2 whitespace-nowrap">{label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * Renders the PayPal Buttons. Each render of this component calls
 * `/api/paypal/create-order` (which resolves the company → returns its
 * Client ID) and dynamically loads the SDK with that Client ID, so the
 * funds flow into the correct merchant account.
 *
 * `keyId` re-mounts the provider when amount/bonus change so PayPal
 * always sees the latest values.
 */
function PayPalRechargeButtons({ token, amount, bonus, summaryAmount, onSuccess }) {
  const [clientId, setClientId] = React.useState(null);
  const [routingRole, setRoutingRole] = React.useState(null);
  const [resolvedTotal, setResolvedTotal] = React.useState(amount);
  const [orderError, setOrderError] = React.useState(null);

  // Pre-flight invariant: provider amount MUST equal Summary amount.
  // If they ever drift (state bug, package-snap residue) we refuse to
  // initialise PayPal so the customer can't be sent to pay the wrong
  // number. Same protection as the Stripe button.
  const amountMismatch = (
    summaryAmount !== undefined &&
    Math.abs(Number(summaryAmount) - Number(amount)) > 0.005
  );

  // Pre-fetch one create-order call to discover the merchant Client ID
  // (so the PayPalScriptProvider can mount with the right one).
  React.useEffect(() => {
    let cancelled = false;
    setClientId(null);
    setOrderError(null);
    if (!amount || amount <= 0) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/paypal/create-order`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount, bonus, description: `Recharge €${amount.toFixed(2)} via PayPal` }),
        });
        if (!r.ok) {
          const t = await r.text();
          throw new Error(t || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (cancelled) return;
        setClientId(j.client_id);
        setRoutingRole(j.routing_role || j.company?.role || null);
        setResolvedTotal(j.total);
        // Hold the order id for createOrder() below
        window.__pendingPayPalOrder = {
          order_id: j.order_id,
          routing_role: j.company?.role,
          total: j.total,
        };
      } catch (e) {
        if (!cancelled) setOrderError(e.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, bonus]);

  if (amountMismatch) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-sm text-red-700" data-testid="paypal-amount-mismatch">
        Internal error: Summary shows €{Number(summaryAmount).toFixed(2)} but PayPal would charge €{Number(amount).toFixed(2)}. Please reload the page.
      </div>
    );
  }
  if (orderError) {
    return (
      <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-sm text-red-700" data-testid="paypal-error">
        Could not initialise PayPal: {orderError}
      </div>
    );
  }
  if (!clientId) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 text-sm text-slate-600" data-testid="paypal-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Preparing PayPal payment for €{amount.toFixed(2)}…
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-amber-300 rounded-xl p-4 sm:p-5 shadow-md" data-testid="paypal-buttons-wrap">
      {/* Call-to-action banner — bold, amber, with an animated arrow
          pointing at the PayPal button. Replaces the previous tiny
          grey hint text that customers were missing. */}
      <div className="mb-3 flex items-center gap-2.5 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
        <span className="text-amber-600 text-lg leading-none animate-bounce">↓</span>
        <p className="text-sm font-bold text-slate-800 leading-snug">
          Click the PayPal button to pay
          <span className="ml-1 text-amber-600">€{resolvedTotal.toFixed(2)}</span>
        </p>
      </div>
      <PayPalScriptProvider
        options={{
          clientId,
          currency: 'EUR',
          intent: 'capture',
          components: 'buttons',
          // Hide the "Debit or Credit Card" button — admin requested
          // that recharges go through the PayPal button only. Other
          // alternative funding sources (Venmo, paylater, etc.) are
          // disabled too so the wallet UI stays a single yellow button.
          'disable-funding': 'card,credit,paylater,venmo,sofort,bancontact,blik,eps,giropay,ideal,mybank,p24,sepa',
        }}
      >
        <PayPalButtons
          style={{ layout: 'vertical', shape: 'rect', label: 'paypal' }}
          fundingSource="paypal"
          forceReRender={[amount, bonus, clientId]}
          createOrder={async () => {
            // We already have a pre-created order from the effect; if it's stale (amount changed),
            // create a fresh one now.
            const cached = window.__pendingPayPalOrder;
            if (cached && Math.abs((cached.total || 0) - resolvedTotal) < 0.005) {
              return cached.order_id;
            }
            const r = await fetch(`${API_URL}/api/paypal/create-order`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ amount, bonus, description: `Recharge €${amount.toFixed(2)} via PayPal` }),
            });
            if (!r.ok) throw new Error(await r.text());
            const j = await r.json();
            window.__pendingPayPalOrder = { order_id: j.order_id, routing_role: j.company?.role, total: j.total };
            setRoutingRole(j.company?.role);
            return j.order_id;
          }}
          onApprove={async (data) => {
            const role = (window.__pendingPayPalOrder && window.__pendingPayPalOrder.routing_role) || routingRole;
            const r = await fetch(`${API_URL}/api/paypal/capture-order`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({
                paypal_order_id: data.orderID,
                routing_role: role,
                amount, bonus,
                description: `Recharge €${amount.toFixed(2)} via PayPal`,
              }),
            });
            if (!r.ok) {
              const t = await r.text();
              alert('PayPal capture failed: ' + t);
              return;
            }
            const j = await r.json();
            window.__pendingPayPalOrder = null;
            onSuccess(j);
          }}
          onError={(err) => {
            alert('PayPal error: ' + (err?.message || err));
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}

/** Stripe hosted-checkout button — canonical-amount flow.
 *
 * Flow:
 *   1. POST /api/stripe/create-checkout-session { amount, origin }
 *      → returns { checkout_url, session_id, amount, bonus }
 *   2. Pre-flight assertion: response.amount === amount passed in props.
 *      If they ever differ we ABORT and surface a hard error instead of
 *      silently sending the user to Stripe to pay a different number.
 *   3. window.location = checkout_url  (redirect to Stripe)
 *   4. Stripe redirects back to /?rp_sid=... — the poller picks it up
 *      and credits the wallet via webhook.
 *
 * The server is the single source of truth for both the charge amount AND
 * the bonus tier. The `bonus` prop here is purely informational (for the
 * pre-pay label); the actual bonus applied is whatever the server computes
 * + the negative-balance rule.
 */
function StripeRechargeButton({ token, amount, bonus, summaryAmount, minRecharge }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Floor enforced inside this sub-component too — server still validates,
  // but blocking the button locally avoids a needless Stripe round-trip.
  const _min = Number.isFinite(minRecharge) ? minRecharge : DEFAULT_MIN_RECHARGE;

  const onPay = async () => {
    setError('');
    const a = Number(amount);
    if (!Number.isFinite(a) || a < _min) {
      setError(`Minimum recharge amount is €${_min}`);
      return;
    }
    // Pre-flight invariant: visible Summary amount MUST equal what we
    // ship to the provider. Catches state-drift bugs before a customer
    // is sent to pay a wrong number.
    if (summaryAmount !== undefined && Math.abs(Number(summaryAmount) - a) > 0.005) {
      setError(`Internal error: Summary shows €${Number(summaryAmount).toFixed(2)} but Stripe would charge €${a.toFixed(2)}. Please reload the page.`);
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const r = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: a, origin }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(t);
      }
      const j = await r.json();
      // Server-side echo assertion — last line of defence against any
      // stray "snap" logic anywhere up the stack.
      if (Math.abs(Number(j.amount) - a) > 0.005) {
        throw new Error(`Stripe returned amount €${j.amount} for our €${a.toFixed(2)} request. Payment cancelled for your safety.`);
      }
      window.location.href = j.checkout_url;
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const displayAmount = Number(amount) || 0;
  return (
    <div className="bg-white border-2 border-indigo-200 rounded-xl p-4 sm:p-5" data-testid="stripe-button-wrap">
      <div className="text-xs text-slate-500 mb-2 font-medium" data-testid="stripe-amount-line">
        You'll be redirected to Stripe's secure hosted checkout for your payment of {" "}
        <b className="text-slate-800" data-testid="stripe-amount-value">€{displayAmount.toFixed(2)}</b>
        {bonus > 0 && <> (plus <b className="text-green-600">+€{Number(bonus).toFixed(2)} bonus</b> after payment)</>}.
      </div>
      <button
        onClick={onPay}
        disabled={loading || displayAmount < _min}
        data-testid="stripe-pay-btn"
        className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-slate-300 disabled:to-slate-400 text-white font-bold py-3 rounded-lg shadow-md transition"
      >
        {loading
          ? (<><Loader2 className="w-5 h-5 animate-spin" /> Redirecting to Stripe…</>)
          : (<>Pay €{displayAmount.toFixed(2)} with Stripe →</>)}
      </button>
      {error && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="stripe-error">
          {error}
        </div>
      )}
      <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-slate-400">
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" /></svg>
        Powered by Stripe · PCI-DSS Level 1
      </div>
    </div>
  );
}
